import React from "react";
import { Box, Stack, Typography, chipClasses } from "@mui/material";
import { styled } from "@mui/material/styles";
import { HyroTable } from "@hyro-components";
import Chip from "@hyro-components/Chip";
import palette from "src/theme/palette";
import { USAGE_BAR_COLORS } from "./SubscriptionTab.consts";

const UsageTrack = styled(Stack)({
  width: 250,
  height: 16,
  borderRadius: 2,
  overflow: "hidden",
  flexDirection: "row",
  backgroundColor: USAGE_BAR_COLORS.track
});

const UsageBar = ({ value }) => {
  const barColor = value > 100 ? USAGE_BAR_COLORS.over : USAGE_BAR_COLORS.under;

  return (
    <Stack direction="row" alignItems="center" gap={2} width="100%">
      <UsageTrack>
        <Box sx={{ width: `${Math.min(value, 100)}%`, bgcolor: barColor, height: "100%" }} />
      </UsageTrack>
      <Typography variant="body2" sx={{ minWidth: 45, textAlign: "right" }}>
        {value < 1 && value > 0 ? value.toFixed(1) : Math.round(value)}%
      </Typography>
    </Stack>
  );
};

const RENDER_CELLS = {
  interaction: ({ value }) => value ? (
    <Chip
      label={value}
      isRounded
      backgroundColorPath={["system", "0"]}
      textColorPath={["text", "800"]}
      sx={{
        border: 1,
        borderColor: palette.system[400],
        height: "auto",
        [`& .${chipClasses.label}`]: { px: "10px", py: "2px" }
      }}
    />
  ) : "—",
  limit: ({ value }) => value != null ? value.toLocaleString() : "—",
  currentUsage: ({ value }) => value != null ? value.toLocaleString() : "—",
  usagePercent: ({ value }) => <UsageBar value={value} />
};

const COLUMNS = [
  { field: "type", headerName: "Type", width: 180 },
  { field: "interaction", headerName: "Interaction", width: 180, renderCell: RENDER_CELLS.interaction },
  { field: "limit", headerName: "Limit", width: 180, renderCell: RENDER_CELLS.limit },
  { field: "currentUsage", headerName: "Current Usage", width: 180, renderCell: RENDER_CELLS.currentUsage },
  { field: "usagePercent", headerName: "Usage %", flex: 1, renderCell: RENDER_CELLS.usagePercent }
];

const buildQuotaRows = (usage, interactionLimit, interactionDefinition) => {
  if (!usage?.length) return [];

  const rows = [];

  usage.forEach((entry) => {
    const hasQuotaData = interactionLimit != null && interactionDefinition != null;
    const currentUsage = !hasQuotaData ? null
      : interactionDefinition === "All" ? entry.total
        : entry.engaged;

    rows.push({
      id: `${entry.channelType}`,
      type: entry.channelType,
      interaction: interactionDefinition || null,
      limit: interactionLimit ?? null,
      currentUsage,
      usagePercent: hasQuotaData && interactionLimit > 0 ? (currentUsage / interactionLimit) * 100 : 0
    });
  });

  return rows;
};

const QuotasTable = ({ opportunity, interactionLimit, interactionDefinition }) => {
  const rows = buildQuotaRows(opportunity.usage, interactionLimit, interactionDefinition);

  if (!rows.length) return null;

  return (
    <Stack gap="12px">
      <Typography variant="subtitle2">
        Quotas usage{" "}
        <Typography component="span" variant="subtitle2" color="text.700">
          ({rows.length})
        </Typography>
      </Typography>
      <HyroTable
        columns={COLUMNS}
        rows={rows}
        minHeight="auto"
      />
    </Stack>
  );
};

export default QuotasTable;
